/**
 * Fairway Crooks - Predictive Search Handler
 * Handles real-time search suggestions and overlay UI
 */

class SearchOverlay {
  constructor() {
    this.overlay = document.getElementById('SearchOverlay');
    this.input = this.overlay?.querySelector('[data-search-input]');
    this.resultsContainer = this.overlay?.querySelector('[data-search-results]');
    this.timer = null;
    this.init();
  }

  init() {
    if (!this.overlay || !this.input) return;

    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      const query = this.input.value.trim();
      
      if (query.length < 2) {
        this.resultsContainer.innerHTML = '';
        return;
      }

      this.timer = setTimeout(() => this.performSearch(query), 300);
    });

    // Handle close on ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay.getAttribute('data-open') === 'true') {
        window.themeModals?.close('search-overlay');
      }
    });
  }

  async performSearch(query) {
    this.resultsContainer.classList.add('opacity-50');
    
    try {
      const response = await fetch(`${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`);
      
      // Note: We can either use the JSON response or the Section Rendering API.
      // JSON is faster for simple lists. Section Rendering is better for styled cards.
      // Let's try the JSON approach first for speed.
      
      const data = await response.json();
      const products = data.resources.results.products;
      
      this.renderResults(products);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      this.resultsContainer.classList.remove('opacity-50');
    }
  }

  escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  renderResults(products) {
    if (!products || products.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="py-10 text-center uppercase tracking-widest text-xs opacity-40">
          No matches found in the archives for this inquiry.
        </div>
      `;
      return;
    }

    const html = `
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        ${products.map(product => `
          <a href="${product.url}" class="group flex flex-col gap-4">
            <div class="aspect-[3/4] bg-surface-container overflow-hidden">
              ${product.image
                ? `<img src="${product.image}" alt="${this.escHtml(product.title)}" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105">`
                : `<div class="w-full h-full bg-surface-container flex items-center justify-center opacity-30"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>`
              }
            </div>
            <div class="flex flex-col gap-1">
              <h4 class="font-heading text-lg uppercase leading-tight">${this.escHtml(product.title)}</h4>
              <p class="text-[10px] uppercase tracking-widest opacity-40">${this.escHtml(product.vendor)}</p>
              <p class="text-sm font-bold mt-1">${product.price_min_as_string || ''}</p>
            </div>
          </a>
        `).join('')}
      </div>
      <div class="mt-12 text-center">
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(this.input.value)}" class="text-[10px] font-bold uppercase tracking-[0.3em] hover:text-secondary transition-colors pb-1 border-b border-current">
          View all results
        </a>
      </div>
    `;
    
    this.resultsContainer.innerHTML = html;
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  window.searchOverlay = new SearchOverlay();
});
